/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * CSV Export Hook
 *
 * React hook that provides CSV export functionality with toast notifications
 * and error handling. Integrates with the file download hook.
 */

import {
  arrayToCSV,
  generateCSVFilename,
  objectArrayToCSV,
  type CSVExportOptions,
  type CSVField,
} from "@/lib/utils/export/csv-export";
import { logger } from "@/lib/utils/logger";
import { toast } from "sonner";
import { useFileDownload } from "./use-file-download";

export interface UseCSVExportOptions extends CSVExportOptions {
  /** Custom success message */
  successMessage?: string;
  /** Custom error message */
  errorMessage?: string;
  /** Whether to show toast notifications */
  showToasts?: boolean;
}

export interface CSVExportHook {
  /** Export array of objects using field definitions */
  exportWithFields: <T extends Record<string, unknown>>(
    data: T[],
    fields: CSVField<T>[],
    baseName: string,
    options?: UseCSVExportOptions,
  ) => Promise<void>;

  /** Export array of objects using object keys as headers */
  exportObjects: <T extends Record<string, unknown>>(
    data: T[],
    baseName: string,
    headerMap?: Partial<Record<keyof T, string>>,
    options?: UseCSVExportOptions,
  ) => Promise<void>;

  /** Export pre-formatted CSV string */
  exportCSV: (
    csvContent: string,
    filename: string,
    options?: Pick<
      UseCSVExportOptions,
      "successMessage" | "errorMessage" | "showToasts"
    >,
  ) => Promise<void>;
}

export function useCSVExport(): CSVExportHook {
  const { downloadCSV } = useFileDownload();

  const exportWithFields = async <T extends Record<string, unknown>>(
    data: T[],
    fields: CSVField<T>[],
    baseName: string,
    options: UseCSVExportOptions = {},
  ) => {
    const {
      successMessage = "Export successful",
      errorMessage = "Export failed",
      showToasts = true,
      ...csvOptions
    } = options;

    try {
      if (data.length === 0) {
        if (showToasts) {
          toast.error("No data to export", {
            description: `There are no ${baseName.toLowerCase()} to export.`,
          });
        }
        return;
      }

      const csvContent = arrayToCSV(data, fields);
      const filename = generateCSVFilename(baseName, csvOptions);

      await downloadCSV(csvContent, filename);

      if (showToasts) {
        toast.success(successMessage, {
          description: `${baseName} exported successfully.`,
        });
      }
    } catch (error) {
      logger.error(`Failed to export ${baseName}:`, error);

      if (showToasts) {
        toast.error(errorMessage, {
          description:
            error instanceof Error
              ? error.message
              : `Failed to export ${baseName.toLowerCase()}`,
        });
      }
      throw error;
    }
  };

  const exportObjects = async <T extends Record<string, unknown>>(
    data: T[],
    baseName: string,
    headerMap?: Partial<Record<keyof T, string>>,
    options: UseCSVExportOptions = {},
  ) => {
    const {
      successMessage = "Export successful",
      errorMessage = "Export failed",
      showToasts = true,
      ...csvOptions
    } = options;

    try {
      if (data.length === 0) {
        if (showToasts) {
          toast.error("No data to export", {
            description: `There are no ${baseName.toLowerCase()} to export.`,
          });
        }
        return;
      }

      const csvContent = objectArrayToCSV(data, headerMap);
      const filename = generateCSVFilename(baseName, csvOptions);

      await downloadCSV(csvContent, filename);

      if (showToasts) {
        toast.success(successMessage, {
          description: `${baseName} exported successfully.`,
        });
      }
    } catch (error) {
      logger.error(`Failed to export ${baseName}:`, error);

      if (showToasts) {
        toast.error(errorMessage, {
          description:
            error instanceof Error
              ? error.message
              : `Failed to export ${baseName.toLowerCase()}`,
        });
      }
      throw error;
    }
  };

  const exportCSV = async (
    csvContent: string,
    filename: string,
    options: Pick<
      UseCSVExportOptions,
      "successMessage" | "errorMessage" | "showToasts"
    > = {},
  ) => {
    const {
      successMessage = "Export successful",
      errorMessage = "Export failed",
      showToasts = true,
    } = options;

    try {
      await downloadCSV(csvContent, filename);

      if (showToasts) {
        toast.success(successMessage, {
          description: "Data exported successfully.",
        });
      }
    } catch (error) {
      logger.error("Failed to export CSV:", error);

      if (showToasts) {
        toast.error(errorMessage, {
          description:
            error instanceof Error ? error.message : "Failed to export data",
        });
      }
      throw error;
    }
  };

  return {
    exportWithFields,
    exportObjects,
    exportCSV,
  };
}
